import React, { useState, useEffect, useMemo } from 'react';
import { adminAPI, getMediaUrl, getMediaType, isVideoFile, getAdminGalleryFolders } from '../../services/api.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';

const GalleryManager = () => {
    const [images, setImages] = useState([]);
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editDescription, setEditDescription] = useState({ de: '', en: '', it: '' });
    const [editFolderId, setEditFolderId] = useState(null);
    const [folderFilter, setFolderFilter] = useState('ALL'); // 'ALL' | 'NONE' | folder.id
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadDescriptions, setUploadDescriptions] = useState({ de: '', en: '', it: '' });
    const [uploadFolderId, setUploadFolderId] = useState('');
    const [activeUploadLang, setActiveUploadLang] = useState('de');

    const { isAuthenticated } = useAuth();

    useEffect(() => {
        fetchImages();
    }, []);

    useEffect(() => {
        // load folders only if authenticated
        if (isAuthenticated) {
            fetchFolders();
        }
    }, [isAuthenticated]);

    const fetchImages = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getGalleryImages();
            setImages(response.data);
        } catch (error) {
            console.error('Error fetching gallery images:', error);
            setError('Fehler beim Laden der Galerie-Medien');
        } finally {
            setLoading(false);
        }
    };

    const fetchFolders = async () => {
        try {
            const token = localStorage.getItem('authToken');
            if (!token) return;
            const response = await getAdminGalleryFolders(token);
            setFolders(response);
        } catch (error) {
            console.error('Error fetching folders:', error);
        }
    };

    const handleMediaUpload = async (file, descriptions = { de: '', en: '', it: '' }, folderId = null) => {
        if (!file) return;

        setUploading(true);
        setError('');

        try {
            const response = await adminAPI.uploadGalleryMedia(file, descriptions, folderId);
            setImages([response.data, ...images]);
        } catch (error) {
            console.error('Error uploading media:', error);
            setError(`Fehler beim Hochladen: ${error.response?.data?.detail || error.message}`);
        } finally {
            setUploading(false);
        }
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;
        const fileExtension = file.name.toLowerCase().split('.').pop();
        const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'mp4', 'webm', 'ogg', 'ogv', 'avi', 'mov', 'wmv', 'flv', '3gp', 'mkv'];
        if (!allowedExtensions.includes(fileExtension)) {
            setError(`Ungültiger Dateityp. Erlaubt sind: ${allowedExtensions.join(', ')}`);
            return;
        }
        setUploadFile(file);
        setUploadDescriptions({ de: '', en: '', it: '' });
        setUploadFolderId('');
        setActiveUploadLang('de');
        setShowUploadModal(true);
        event.target.value = '';
    };

    const resetUploadState = () => {
        setShowUploadModal(false);
        setUploadFile(null);
        setUploadDescriptions({ de: '', en: '', it: '' });
        setUploadFolderId('');
    };

    const performUpload = async () => {
        if (!uploadFile) return;
        await handleMediaUpload(uploadFile, uploadDescriptions, uploadFolderId || null);
        resetUploadState();
    };

    const handleDeleteImage = async (imageId) => {
        if (!window.confirm('Sind Sie sicher, dass Sie dieses Bild löschen möchten?')) {
            return;
        }

        try {
            await adminAPI.deleteGalleryImage(imageId);
            setImages(images.filter(img => img.id !== imageId));
        } catch (error) {
            console.error('Error deleting image:', error);
            setError(`Fehler beim Löschen: ${error.response?.data?.detail || error.message}`);
        }
    };

    const handleEditDescription = (image) => {
        setEditingId(image.id);
        setEditDescription({
            de: image.description_de || '',
            en: image.description_en || '',
            it: image.description_it || '',
        });
        setEditFolderId(image.folder_id);
    };

    const handleSaveDescription = async (imageId) => {
        try {
            const trimmed = {
                de: (editDescription.de || '').trim(),
                en: (editDescription.en || '').trim(),
                it: (editDescription.it || '').trim(),
            };
            const response = await adminAPI.updateGalleryImage(imageId, trimmed, editFolderId);
            setImages(images.map(img => (img.id === imageId ? response.data : img)));
            setEditingId(null);
            setEditDescription({ de: '', en: '', it: '' });
            setEditFolderId(null);
        } catch (error) {
            console.error('Error updating description:', error);
            setError(`Fehler beim Aktualisieren: ${error.response?.data?.detail || error.message}`);
        }
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditDescription({ de: '', en: '', it: '' });
        setEditFolderId(null);
    };

    const renderMediaThumbnail = (item) => {
        const mediaType = getMediaType(item);
        const mediaUrl = getMediaUrl(item.filename); // Remove uploads/ prefix
        const folderObj = item.folder_id ? folders.find(f => f.id === item.folder_id) : null;

        if (mediaType === 'video') {
            return (
                <div className="relative group">
                    <video
                        className="w-full h-48 object-cover cursor-pointer bg-black group-hover:brightness-[.85] transition"
                        onClick={() => setSelectedImage(item)}
                        muted
                        preload="metadata"
                        playsInline
                        crossOrigin="anonymous"
                        onError={(e) => console.error('Admin video error:', e)}
                        onCanPlay={() => console.log('Admin video can play:', mediaUrl)}
                    >
                        <source src={`${mediaUrl}#t=0.1`} type={item.mime_type || 'video/mp4'} />
                    </video>
                    {/* Video indicator */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="bg-black/50 rounded-full p-3 backdrop-blur-sm">
                            <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                        </div>
                    </div>
                    {folderObj && (
                        <div className="absolute top-2 left-2 px-2 py-1 rounded-md text-[10px] font-medium bg-black/60 text-white backdrop-blur-sm max-w-[70%] truncate">
                            {folderObj.description_de || folderObj.description_en || folderObj.description_it || folderObj.id}
                        </div>
                    )}
                    {/* Remove zoom overlay for videos */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteImage(item.id);
                        }}
                        className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-1 rounded-full transition-colors"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            );
        } else {
            return (
                <div className="relative group">
                    <img
                        src={mediaUrl}
                        alt={item.original_filename}
                        className="w-full h-48 object-cover cursor-pointer group-hover:scale-[1.02] group-hover:brightness-95 transition"
                        onClick={() => setSelectedImage(item)}
                        loading="lazy"
                        crossOrigin="anonymous"
                    />
                    {/* Keep zoom overlay only for images */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <svg className="w-7 h-7 text-white drop-shadow" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" /></svg>
                    </div>
                    {folderObj && (
                        <div className="absolute top-2 left-2 px-2 py-1 rounded-md text-[10px] font-medium bg-black/60 text-white backdrop-blur-sm max-w-[70%] truncate">
                            {folderObj.description_de || folderObj.description_en || folderObj.description_it || folderObj.id}
                        </div>
                    )}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteImage(item.id);
                        }}
                        className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-1 rounded-full transition-colors"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            );
        }
    };

    const renderModalMedia = (item) => {
        const mediaType = getMediaType(item);
        const mediaUrl = getMediaUrl(item.filename); // Remove uploads/ prefix

        if (mediaType === 'video') {
            return (
                <video
                    className="max-w-full max-h-[60vh] object-contain mx-auto"
                    controls
                    autoPlay
                    muted
                    playsInline
                    preload="metadata"
                    crossOrigin="anonymous"
                    onError={(e) => console.error('Admin modal video error:', e)}
                    onCanPlay={() => console.log('Admin modal video can play:', mediaUrl)}
                >
                    <source src={mediaUrl} type={item.mime_type || 'video/mp4'} />
                    Your browser does not support the video tag.
                </video>
            );
        } else {
            return (
                <img
                    src={mediaUrl}
                    alt={item.original_filename}
                    className="max-w-full max-h-[60vh] object-contain mx-auto"
                    crossOrigin="anonymous"
                />
            );
        }
    };

    const filteredImages = useMemo(() => {
        if (folderFilter === 'ALL') return images;
        if (folderFilter === 'NONE') return images.filter(img => !img.folder_id);
        return images.filter(img => img.folder_id === folderFilter);
    }, [images, folderFilter]);

    // Drag & Drop handlers
    const [draggingId, setDraggingId] = useState(null);

    const onDragStart = (e, image) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', image.id.toString());
        setDraggingId(image.id);
    };

    const onDragEnd = () => setDraggingId(null);

    const handleFolderDrop = async (e, targetFolderId) => {
        e.preventDefault();
        const id = parseInt(e.dataTransfer.getData('text/plain'));
        if (!id) return;
        try {
            const img = images.find(i => i.id === id);
            if (!img) return;
            if ((img.folder_id || null) === (targetFolderId || null)) return; // no change
            const resp = await adminAPI.updateGalleryImage(id, { de: img.description_de ?? null, en: img.description_en ?? null, it: img.description_it ?? null }, targetFolderId);
            setImages(prev => prev.map(i => i.id === id ? resp.data : i));
        } catch (err) {
            console.error('Folder assignment failed', err);
            setError('Ordner-Zuordnung fehlgeschlagen');
        } finally {
            setDraggingId(null);
        }
    };

    const allowDrop = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-neutral-950">Galerie verwalten</h2>
                <div className="flex items-center space-x-4">
                    <label className="cursor-pointer">
                        <input
                            type="file"
                            accept="image/*,video/*"
                            onChange={handleFileSelect}
                            disabled={uploading}
                            className="hidden"
                        />
                        <span className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-colors ${uploading
                            ? 'bg-neutral-400 cursor-not-allowed'
                            : 'bg-primary-600 hover:bg-primary-700'
                            }`}>
                            {uploading ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Wird hochgeladen...
                                </>
                            ) : (
                                <>
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                    </svg>
                                    Medium hochladen
                                </>
                            )}
                        </span>
                    </label>
                    <button
                        onClick={fetchImages}
                        className="bg-neutral-600 hover:bg-neutral-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                    >
                        Aktualisieren
                    </button>
                </div>
            </div>

            {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                    {error}
                </div>
            )}

            <div className="space-y-3 mb-4">
                <div className="text-sm text-neutral-600">
                    {filteredImages.length} / {images.length} Medien sichtbar
                    <div className="text-xs text-neutral-500 mt-1">
                        Unterstützte Formate: Bilder (JPG, PNG, GIF, WebP, BMP) • Videos (MP4, WebM, OGG, AVI, MOV, WMV)
                    </div>
                </div>
                {/* Folder Filter Chips */}
                {folders.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto py-1 scrollbar-thin scrollbar-thumb-neutral-300 pr-2">
                        {[
                            { id: 'ALL', label: 'Alle' },
                            { id: 'NONE', label: 'Ohne Ordner' },
                            ...folders.map(f => ({ id: f.id, label: f.description_de || f.description_en || f.description_it || `Ordner ${f.id}` }))
                        ].map(chip => {
                            const active = folderFilter === chip.id;
                            return (
                                <button
                                    key={chip.id}
                                    onClick={() => setFolderFilter(chip.id)}
                                    onDragOver={allowDrop}
                                    onDrop={(e) => {
                                        if (chip.id === 'ALL') return; // can't drop on ALL pseudo-filter
                                        const folderTarget = chip.id === 'NONE' ? null : chip.id;
                                        handleFolderDrop(e, folderTarget);
                                    }}
                                    className={`relative flex items-center gap-1 px-3 h-8 rounded-full text-xs font-medium whitespace-nowrap border transition-all ${active
                                        ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                                        : 'bg-white text-neutral-600 border-neutral-200 hover:border-primary-400 hover:text-neutral-800'} ${draggingId && chip.id !== 'ALL' ? 'after:absolute after:inset-0 after:rounded-full after:ring-2 after:ring-dashed after:ring-primary-400/40' : ''}`}
                                >
                                    {chip.label}
                                    {chip.id !== 'ALL' && (
                                        <span className={`ml-1 inline-block px-1.5 py-0.5 rounded-full text-[10px] leading-none ${active ? 'bg-white/20 text-white' : 'bg-neutral-100 text-neutral-500'}`}>
                                            {chip.id === 'NONE'
                                                ? images.filter(i => !i.folder_id).length
                                                : images.filter(i => i.folder_id === chip.id).length}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {images.length === 0 ? (
                <div className="text-center py-12">
                    <div className="text-neutral-500">
                        <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p>Keine Medien in der Galerie</p>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredImages.map((image) => {
                        const mediaType = getMediaType(image);
                        return (
                            <div
                                key={image.id}
                                className={`bg-white rounded-xl border border-neutral-200 overflow-hidden shadow-sm ${draggingId === image.id ? 'opacity-70 ring-2 ring-primary-400' : 'hover:shadow-md transition-shadow'}`}
                                draggable
                                onDragStart={(e) => onDragStart(e, image)}
                                onDragEnd={onDragEnd}
                            >
                                {renderMediaThumbnail(image)}
                                <div className="p-4">
                                    <div className="flex items-center text-sm text-neutral-600 mb-2">
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mr-2 ${mediaType === 'video'
                                            ? 'bg-blue-100 text-blue-800'
                                            : 'bg-green-100 text-green-800'
                                            }`}>
                                            {mediaType === 'video' ? (
                                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M8 5v14l11-7z" />
                                                </svg>
                                            ) : (
                                                <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                            )}
                                            {mediaType === 'video' ? 'Video' : 'Bild'}
                                        </span>
                                        {image.original_filename}
                                    </div>
                                    <div className="text-xs text-neutral-500 mb-3">
                                        {new Date(image.upload_date).toLocaleDateString('de-DE')} • {Math.round(image.file_size / 1024)} KB
                                    </div>
                                    {editingId === image.id ? (
                                        <div className="space-y-2 border-t pt-3 mt-3">
                                            <label className="block text-xs font-medium text-neutral-600">Deutsch</label>
                                            <textarea
                                                value={editDescription.de}
                                                onChange={(e) => setEditDescription(s => ({ ...s, de: e.target.value }))}
                                                className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                placeholder="Beschreibung (DE)..."
                                                rows={2}
                                            />
                                            <label className="block text-xs font-medium text-neutral-600">Englisch</label>
                                            <textarea
                                                value={editDescription.en}
                                                onChange={(e) => setEditDescription(s => ({ ...s, en: e.target.value }))}
                                                className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                placeholder="Beschreibung (EN)..."
                                                rows={2}
                                            />
                                            <label className="block text-xs font-medium text-neutral-600">Italienisch</label>
                                            <textarea
                                                value={editDescription.it}
                                                onChange={(e) => setEditDescription(s => ({ ...s, it: e.target.value }))}
                                                className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                placeholder="Beschreibung (IT)..."
                                                rows={2}
                                            />
                                            <label className="block text-xs font-medium text-neutral-600 mt-2">Ordner</label>
                                            <select
                                                value={editFolderId || ''}
                                                onChange={(e) => setEditFolderId(e.target.value ? parseInt(e.target.value) : null)}
                                                className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                            >
                                                <option value="">Kein Ordner</option>
                                                {folders.map(folder => (
                                                    <option key={folder.id} value={folder.id}>
                                                        {folder.description_de || folder.id}
                                                    </option>
                                                ))}
                                            </select>
                                            <div className="flex space-x-2 mt-2">
                                                <button
                                                    onClick={() => handleSaveDescription(image.id)}
                                                    className="px-3 py-1 bg-primary-600 text-white text-sm rounded hover:bg-primary-700 transition-colors"
                                                >
                                                    Speichern
                                                </button>
                                                <button
                                                    onClick={handleCancelEdit}
                                                    className="px-3 py-1 bg-neutral-300 text-neutral-700 text-sm rounded hover:bg-neutral-400 transition-colors"
                                                >
                                                    Abbrechen
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="space-y-1 border-t pt-3 mt-3">
                                            <div className="text-sm text-neutral-700 min-h-[20px]">
                                                <span className="font-medium text-neutral-800">DE: </span>{image.description_de || '—'}
                                            </div>
                                            <div className="text-sm text-neutral-700 min-h-[20px]">
                                                <span className="font-medium text-neutral-800">EN: </span>{image.description_en || '—'}
                                            </div>
                                            <div className="text-sm text-neutral-700 min-h-[20px]">
                                                <span className="font-medium text-neutral-800">IT: </span>{image.description_it || '—'}
                                            </div>
                                            <div className="text-sm text-neutral-700 min-h-[20px]">
                                                <span className="font-medium text-neutral-800">Ordner: </span>
                                                {image.folder_id ? (folders.find(f => f.id === image.folder_id)?.description_de || image.folder_id) : '—'}
                                            </div>
                                            <button
                                                onClick={() => handleEditDescription(image)}
                                                className="mt-2 text-sm text-primary-600 hover:text-primary-700 transition-colors"
                                            >
                                                Beschreibung bearbeiten
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Media Preview Modal */}
            {selectedImage && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden">
                        <div className="flex justify-between items-center p-4 border-b">
                            <div>
                                <h3 className="text-lg font-semibold">{selectedImage.original_filename}</h3>
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-1 ${getMediaType(selectedImage) === 'video'
                                    ? 'bg-blue-100 text-blue-800'
                                    : 'bg-green-100 text-green-800'
                                    }`}>
                                    {getMediaType(selectedImage) === 'video' ? 'Video' : 'Bild'}
                                </span>
                            </div>
                            <button
                                onClick={() => setSelectedImage(null)}
                                className="text-neutral-500 hover:text-neutral-700"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-4">
                            {renderModalMedia(selectedImage)}
                            {(selectedImage.description_de || selectedImage.description_en || selectedImage.description_it) && (
                                <div className="mt-4 text-center space-y-1 text-sm text-neutral-700">
                                    {selectedImage.description_de && <p><strong>DE:</strong> {selectedImage.description_de}</p>}
                                    {selectedImage.description_en && <p><strong>EN:</strong> {selectedImage.description_en}</p>}
                                    {selectedImage.description_it && <p><strong>IT:</strong> {selectedImage.description_it}</p>}
                                    {selectedImage.folder_id && <p><strong>Ordner:</strong> {folders.find(f => f.id === selectedImage.folder_id)?.description_de || selectedImage.folder_id}</p>}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showUploadModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <h3 className="text-lg font-semibold">Neues Medium hochladen</h3>
                            <button onClick={resetUploadState} className="text-neutral-500 hover:text-neutral-700">
                                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 18 18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
                            {uploadFile ? (
                                <div className="flex flex-col md:flex-row gap-6">
                                    <div className="md:w-1/2 space-y-4">
                                        <div className="relative w-full aspect-video rounded-xl bg-neutral-100 border border-neutral-200 flex items-center justify-center overflow-hidden">
                                            {isVideoFile(uploadFile.name) ? (
                                                <video className="h-full w-full object-cover" src={URL.createObjectURL(uploadFile)} muted playsInline />
                                            ) : (
                                                <img src={URL.createObjectURL(uploadFile)} alt="Preview" className="h-full w-full object-cover" />
                                            )}
                                            <div className="absolute bottom-2 left-2 px-2 py-1 text-xs rounded-md bg-black/50 text-white backdrop-blur-sm">
                                                {isVideoFile(uploadFile.name) ? 'Video' : 'Bild'}
                                            </div>
                                        </div>
                                        <div className="text-xs text-neutral-500 break-all">
                                            Datei: {uploadFile.name} • {(uploadFile.size / 1024).toFixed(1)} KB
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-neutral-600 mb-1">Ordnerzuordnung</label>
                                            <select
                                                value={uploadFolderId}
                                                onChange={(e) => setUploadFolderId(e.target.value)}
                                                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                                            >
                                                <option value="">Kein Ordner</option>
                                                {folders.map(f => (
                                                    <option key={f.id} value={f.id}>{f.description_de || f.description_en || f.description_it || f.id}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="md:w-1/2 space-y-4">
                                        <div className="inline-flex rounded-lg border border-neutral-200 bg-neutral-50 overflow-hidden text-sm">
                                            {['de', 'en', 'it'].map(code => (
                                                <button key={code} type="button" onClick={() => setActiveUploadLang(code)} className={`px-3 py-1.5 font-medium transition-colors ${activeUploadLang === code ? 'bg-white text-neutral-900 shadow-inner' : 'text-neutral-500 hover:text-neutral-800 hover:bg-neutral-100'}`}>{code.toUpperCase()}</button>
                                            ))}
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-neutral-600 mb-1">Beschreibung ({activeUploadLang.toUpperCase()})</label>
                                            <textarea
                                                value={uploadDescriptions[activeUploadLang]}
                                                onChange={(e) => setUploadDescriptions(s => ({ ...s, [activeUploadLang]: e.target.value }))}
                                                rows={4}
                                                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                                                placeholder="Optionale Beschreibung..."
                                            />
                                        </div>
                                        <div className="grid grid-cols-3 gap-3 text-[11px] text-neutral-500">
                                            {['de', 'en', 'it'].map(code => (
                                                <div key={code} className="flex flex-col bg-neutral-50 rounded-md p-2 border border-neutral-200">
                                                    <span className="font-semibold uppercase text-neutral-400 mb-1">{code}</span>
                                                    <span className="line-clamp-3 whitespace-pre-wrap break-words text-neutral-600 min-h-[48px]">{uploadDescriptions[code] || <span className="italic text-neutral-400">leer</span>}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="border-2 border-dashed border-neutral-300 rounded-xl p-10 flex flex-col items-center justify-center text-center bg-neutral-50/50">
                                    <svg className="w-12 h-12 text-neutral-300 mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 16v-8" /><path d="M8 12h8" /><path d="M20 16v-1a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v1" /><path d="M20 8V7a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v1" /></svg>
                                    <p className="text-sm font-medium text-neutral-700 mb-2">Datei wählen oder hierher ziehen</p>
                                    <p className="text-xs text-neutral-500 mb-4">Unterstützt: Bilder (JPG, PNG, GIF, WebP, BMP) & Videos (MP4, WebM, OGG, AVI, MOV, WMV, ...)</p>
                                    <label className="inline-flex">
                                        <input type="file" accept="image/*,video/*" onChange={handleFileSelect} className="hidden" />
                                        <span className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium cursor-pointer shadow-sm">Datei auswählen</span>
                                    </label>
                                </div>
                            )}
                        </div>
                        <div className="border-t bg-neutral-50 px-6 py-4 flex items-center justify-between gap-4">
                            <div className="text-xs text-neutral-500">Felder können nachträglich bearbeitet werden.</div>
                            <div className="flex gap-3">
                                <button onClick={resetUploadState} className="px-4 h-10 rounded-lg bg-neutral-200 hover:bg-neutral-300 text-neutral-800 text-sm font-medium">Abbrechen</button>
                                <button disabled={!uploadFile || uploading} onClick={performUpload} className="px-5 h-10 rounded-lg bg-primary-600 hover:bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium inline-flex items-center gap-2">
                                    {uploading && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>}
                                    Hochladen
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GalleryManager;
